import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAppUIContext } from './AppUIContext';
import { getMemberPermissions, WorkspacePermissions } from '../models/WorkspacePermissions';
import { WorkspaceMemberRole } from '../models/Workspace';
import { Fire } from '../services/FireClient';

interface PermissionState {
    perms: WorkspacePermissions | null;
    role: WorkspaceMemberRole | null;
}

const WorkspacePermissionContext = createContext<PermissionState>({ perms: null, role: null });

/** 현재 유저의 워크스페이스 permissions를 가져옵니다 */
export function useMyPermissions(): WorkspacePermissions | null {
    return useContext(WorkspacePermissionContext).perms;
}

/** 현재 유저의 워크스페이스 역할을 가져옵니다 */
export function useMyRole(): WorkspaceMemberRole | null {
    return useContext(WorkspacePermissionContext).role;
}

/**
 * 워크스페이스 권한 Provider.
 * activeWorkspaceId가 바뀔 때마다 Firestore에서 멤버 권한을 1회 로드.
 */
export const WorkspacePermissionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { activeWorkspaceId } = useAppUIContext();
    const [state, setState] = useState<PermissionState>({ perms: null, role: null });

    useEffect(() => {
        // 워크스페이스 전환 시 즉시 이전 권한을 리셋 (stale perms로 잘못된 쿼리 방지)
        setState({ perms: null, role: null });
        if (!activeWorkspaceId) {
            return;
        }
        Fire.ws.getMyMember(activeWorkspaceId).then(myMember => {
            if (myMember) {
                setState({
                    perms: getMemberPermissions(myMember),
                    role: (myMember.role as WorkspaceMemberRole) ?? 'member',
                });
            } else {
                setState({ perms: null, role: null });
            }
        }).catch(() => setState({ perms: null, role: null }));
    }, [activeWorkspaceId]);

    return (
        <WorkspacePermissionContext.Provider value={state}>
            {children}
        </WorkspacePermissionContext.Provider>
    );
};

export default WorkspacePermissionContext;
